import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EstadoBadge, Pagination, SkeletonRows } from '@aosenuma/ui';
import {
  estadoEfectivoFromGestion,
  fmtRelative,
  LABEL_ESTADO_SEMAFORO,
  tituloParcelaConTitular,
  type EstadoGestion,
  type EstadoSemaforo,
  type LastActivityKind,
  type VBandejaActividad,
} from '@aosenuma/lib-types';
import { isSupabaseConfigured, useBandejaActividad } from '@aosenuma/lib-supabase';

interface FilaBandeja {
  parcela_id: string;
  num_parcela: number | null;
  titular_masked: string | null;
  nom_nucleo: string;
  area_ha: number;
  estado: EstadoSemaforo;
  last_activity_at: string;
  last_activity_kind: LastActivityKind;
  expedientes_count: number;
  irregularidades_count: number;
}

/**
 * Etiqueta semántica para la columna "Último evento". Deriva del
 * `last_activity_kind` clasificado en SQL + counts auxiliares.
 */
function labelUltimoEvento(row: FilaBandeja): string {
  switch (row.last_activity_kind) {
    case 'expediente_upload':
      return row.expedientes_count > 0
        ? `+${row.expedientes_count} archivo(s)`
        : 'Archivo cargado';
    case 'irregularidad_flag':
      return row.irregularidades_count > 0
        ? `+${row.irregularidades_count} irregularidad(es)`
        : 'Irregularidad flageada';
    case 'estado_change':
    default:
      return `Estado: ${LABEL_ESTADO_SEMAFORO[row.estado]}`;
  }
}

function vRowsToBandeja(rows: VBandejaActividad[]): FilaBandeja[] {
  return rows.map((p) => ({
    parcela_id: p.parcela_id,
    num_parcela: p.num_parcela,
    titular_masked: p.titular_masked,
    nom_nucleo: p.nom_nucleo ?? '—',
    area_ha: Number(p.area_ha),
    estado: estadoEfectivoFromGestion(p.estado as EstadoGestion, p.firmado) as EstadoSemaforo,
    last_activity_at: p.last_activity_at,
    last_activity_kind: p.last_activity_kind,
    expedientes_count: p.expedientes_count,
    irregularidades_count: p.irregularidades_count,
  }));
}

const POR_PAGINA = 10;

/** Bucket de "cuándo ocurrió" para alimentar los KPIs. */
function isToday(iso: string, ref: Date = new Date()): boolean {
  const d = new Date(iso);
  return (
    d.getFullYear() === ref.getFullYear() &&
    d.getMonth() === ref.getMonth() &&
    d.getDate() === ref.getDate()
  );
}
function isThisWeek(iso: string, ref: Date = new Date()): boolean {
  const d = new Date(iso);
  const sevenDaysAgo = new Date(ref);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  return d >= sevenDaysAgo;
}

export function BandejaPage() {
  const [pagina, setPagina] = useState(1);
  const { data: apiRows, loading, error } = useBandejaActividad();

  const filasLista: FilaBandeja[] = useMemo(() => vRowsToBandeja(apiRows), [apiRows]);

  useEffect(() => {
    setPagina(1);
  }, [filasLista.length]);

  const totalPags = Math.max(1, Math.ceil(filasLista.length / POR_PAGINA));
  const filas = filasLista.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  /**
   * KPIs reinterpretados (vs los anteriores que decían "Asignadas: 2374"):
   *   Activas        — todas con actividad alguna vez (size de la lista)
   *   Hoy            — last_activity_at en el día actual
   *   Esta semana    — last_activity_at en los últimos 7 días
   *   En negociación — estado === 'en_negociacion' (señal de progreso comercial)
   */
  const counts = useMemo(() => {
    const ref = new Date();
    let hoy = 0;
    let semana = 0;
    let enNegociacion = 0;
    for (const f of filasLista) {
      if (isToday(f.last_activity_at, ref)) hoy++;
      if (isThisWeek(f.last_activity_at, ref)) semana++;
      if (f.estado === 'en_negociacion') enNegociacion++;
    }
    return {
      activas: filasLista.length,
      hoy,
      semana,
      en_negociacion: enNegociacion,
    };
  }, [filasLista]);

  return (
    <section className="panel">
      <header className="panel-header">
        <h1>Bandeja del día</h1>
        <p className="panel-sub">
          Parcelas con actividad reciente — cambios de estado, archivos cargados o irregularidades.
        </p>
      </header>
      <div className="panel-body">
        {error && isSupabaseConfigured() && (
          <p className="panel-sub" style={{ color: '#c96', marginBottom: 12 }}>
            No se pudo cargar la bandeja desde Supabase ({error.message}).
          </p>
        )}

        {loading && isSupabaseConfigured() && apiRows.length === 0 && !error ? (
          <SkeletonRows count={5} rowHeight={56} className="panel-skeleton" />
        ) : null}

        <div
          className="kpi-row"
          style={loading && apiRows.length === 0 ? { opacity: 0.4 } : undefined}
        >
          <div className="kpi-card-eq">
            <div className="label">Activas</div>
            <div className="value">{counts.activas}</div>
          </div>
          <div className="kpi-card-eq">
            <div className="label">Hoy</div>
            <div className="value">{counts.hoy}</div>
          </div>
          <div className="kpi-card-eq">
            <div className="label">Esta semana</div>
            <div className="value">{counts.semana}</div>
          </div>
          <div className="kpi-card-eq">
            <div className="label">En negociación</div>
            <div className="value">{counts.en_negociacion}</div>
          </div>
        </div>

        <div className="bandeja-table glass-panel">
          {filas.length === 0 && !loading ? (
            <div className="empty-state">
              <span className="empty-state__icon" aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.6}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 4h18v16H3z M3 10h18" />
                </svg>
              </span>
              <h3 className="empty-state__title">Aún no hay actividad</h3>
              <p className="empty-state__text">
                Las parcelas aparecerán aquí cuando un técnico capture, cargue archivos o flagee
                irregularidades.
              </p>
            </div>
          ) : (
            <>
              <div className="bandeja-head">
                <span>N.º parcela</span>
                <span>Ejido</span>
                <span>Estado</span>
                <span>Actualización</span>
                <span>Último evento</span>
                <span />
              </div>
              {filas.map((p) => (
                <div className="bandeja-row" key={p.parcela_id}>
                  <div className="bandeja-parcela-cell">
                    <strong className="bandeja-parcela-title">
                      {tituloParcelaConTitular(p.parcela_id, p.num_parcela, p.titular_masked)}
                    </strong>
                    <div className="bandeja-id-sub">{p.parcela_id}</div>
                  </div>
                  <span>{p.nom_nucleo}</span>
                  <EstadoBadge value={p.estado} />
                  <span style={{ color: 'var(--text-dim)' }} title={p.last_activity_at}>
                    {fmtRelative(p.last_activity_at)}
                  </span>
                  <span style={{ color: 'var(--text-dim)' }}>{labelUltimoEvento(p)}</span>
                  <Link to={`/expedientes/${p.parcela_id}`} className="bandeja-open-btn">
                    Abrir
                  </Link>
                </div>
              ))}

              <Pagination current={pagina} total={totalPags} onChange={setPagina} />
            </>
          )}
        </div>
      </div>
    </section>
  );
}
